// Mistakes from the parser's facts, without AI. The rules and scores mirror
// internal/highlights/mistakes.go, which picks the deaths for a mistakes
// video, so the list and the video agree.
import type { Kill, Mistake, MistakeType, Praise, PraiseType, SkillKey } from "./api";

export type DeathFlag = "opening" | "untraded" | "blind" | "utility" | "outnumbered";

export type Death = {
  round: number; tick: number; killer?: string; weapon: string; roundTime?: number;
  flags: DeathFlag[]; score: number; /** one of the deaths a mistakes video would show */ key: boolean;
};

export const FLAG_LABEL: Record<DeathFlag, string> = {
  opening: "открывающая смерть без размена",
  untraded: "без размена",
  blind: "ослеплён",
  utility: "не бросил гранаты",
  outnumbered: "в меньшинстве",
};

/** Same limit as the video (highlights.Deaths default). */
const KEY_DEATHS = 6;

export function deathsOf(kills: Kill[], steamId: string): Death[] {
  const seen = new Set<number>();
  const byRound = new Map<number, { k: Kill; opening: boolean }>();
  for (const k of kills) {
    const first = !seen.has(k.round) && !k.teamKill && !!k.killer && k.killer !== k.victim;
    if (first) seen.add(k.round);
    if (k.victim === steamId && !byRound.has(k.round)) byRound.set(k.round, { k, opening: first });
  }
  const out: Death[] = [];
  for (const [round, { k, opening }] of byRound) {
    const flags: DeathFlag[] = [];
    let score = 0;
    if (opening && !k.victimTraded) {
      score += 3;
      flags.push("opening");
    } else if (!k.victimTraded && (k.aliveMates ?? 0) > 0) {
      score += 1;
      flags.push("untraded");
    }
    if (k.victimBlind) {
      score += 1.5;
      flags.push("blind");
    }
    const nades = k.victimUtility ?? 0;
    if (nades >= 2) {
      score += 1.5;
      flags.push("utility");
    } else if (nades === 1) score += 0.5;
    // Shown for context only; not part of the video's score.
    if ((k.aliveEnemies ?? 0) > (k.aliveMates ?? 0) + 1) flags.push("outnumbered");
    out.push({ round, tick: k.tick, killer: k.killer, weapon: k.weapon, roundTime: k.roundTime, flags, score, key: false });
  }
  [...out]
    .filter((d) => d.score >= 2)
    .sort((a, b) => b.score - a.score || a.round - b.round)
    .slice(0, KEY_DEATHS)
    .forEach((d) => (d.key = true));
  return out.sort((a, b) => a.round - b.round);
}

/** How often each flag happened, most frequent first. */
export function flagCounts(deaths: Death[]) {
  const counts = new Map<DeathFlag, number>();
  for (const d of deaths) for (const f of d.flags) counts.set(f, (counts.get(f) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1]);
}


/**
 * Decision mistakes the parser found (schema v3+): not deaths, but what was
 * done around them — a teammate left untraded, a lurk nobody asked for,
 * utility that stayed in the bag, a rotation that never started.
 */
export const MISTAKE: Record<MistakeType, { title: string; hint: string }> = {
  no_trade: { title: "Не разменял", hint: "Свой умер в вашем же бою — ни размена, ни выстрела в его убийцу" },
  solo: { title: "Играл в отрыве", hint: "К первому контакту вы были далеко от команды" },
  far_from_team: { title: "Долго стоял отдельно", hint: "Держали позицию, пока команда была в другой части карты" },
  slow_rotate: { title: "Не доехал до бомбы", hint: "После плента остались далеко от точки" },
  no_support: { title: "Не дал утилиту", hint: "Сокомандник заходил и умер, а гранаты остались" },
  team_flash: { title: "Ослепил своего", hint: "Флешка засветила сокомандника" },
  backstab: { title: "Не проверил тыл", hint: "Убили со спины, вне поля зрения" },
  aim_low: { title: "Низкая наводка", hint: "Прицел в среднем заметно ниже головы противника" },
  moving_shots: { title: "Стрельба на ходу", hint: "Много выстрелов из винтовки без остановки — первая пуля уже мимо" },
  spray_miss: { title: "Спрей мимо", hint: "Длинная очередь почти без попаданий" },
  overflick: { title: "Перефлик", hint: "Прицел дёргается сильно дальше цели" },
  overpeek: { title: "Выглянул в двоих", hint: "Смерть под огнём двух и более противников" },
  rush_no_info: { title: "Рано умер без инфы", hint: "Смерть в первые секунды с полным набором гранат и без урона" },
  eco_mismatch: { title: "Закуп не с командой", hint: "Купились, когда команда сохранялась — или наоборот" },
  wasted_util: { title: "Гранаты впустую", hint: "Большая часть гранат за матч ничего не дала" },
  jump_shots: { title: "Стрельба в прыжке", hint: "Несколько выстрелов в воздухе за раунд" },
};

/** What went right, in the same voice. */
export const PRAISE: Record<PraiseType, { title: string; hint: string }> = {
  trade: { title: "Разменял", hint: "Наказали убийцу сокомандника в течение пяти секунд" },
  opening: { title: "Открыл раунд", hint: "Выиграли первую дуэль раунда" },
  opening_won: { title: "Открыл и забрали раунд", hint: "Первый фраг раунда — и команда его выиграла" },
  clutch: { title: "Клатч", hint: "Выиграли раунд в одиночку против нескольких" },
  multikill: { title: "Мультикилл", hint: "Три и больше фрагов за раунд" },
  flash_assist: { title: "Флеш-ассист", hint: "Ослепили противника, и его тут же сняли" },
  save: { title: "Сохранил оружие", hint: "Пережили проигранный раунд с винтовкой" },
  clean_spray: { title: "Чистый спрей", hint: "Длинная очередь, где легла большая часть пуль" },
  quick_duel: { title: "Быстрая дуэль", hint: "Фраг с одного-двух выстрелов" },
  util_damage: { title: "Урон гранатами", hint: "40+ урона гранатами за раунд" },
  counter_strafe: { title: "Контрстрейф", hint: "Почти все выстрелы из винтовки — стоя" },
  crosshair: { title: "Наводка на уровне головы", hint: "Прицел держится у головы противника весь матч" },
  support: { title: "Поддержка", hint: "Граната перед заходом сокомандника" },
};

export const SKILL: Record<SkillKey, { title: string; hint: string }> = {
  aim: { title: "Аим", hint: "точность, хедшоты, наводка, спрей" },
  movement: { title: "Мувмент", hint: "контрстрейф, стрельба в воздухе" },
  utility: { title: "Утилита", hint: "ослепления, урон гранатами, флешки по своим" },
  teamplay: { title: "Командная игра", hint: "размены, поддержка, игра вместе" },
  decisions: { title: "Решения", hint: "пики, ротации, закуп, тыл" },
};

/** One player's praise, most valuable first, with counts per kind. */
export function praiseOf(all: Praise[] | undefined, steamId: string) {
  const rank: Record<PraiseType, number> = { clutch: 9, multikill: 8, opening_won: 7, flash_assist: 6, trade: 6, crosshair: 5, counter_strafe: 5, clean_spray: 4, save: 4, util_damage: 4, support: 3, opening: 3, quick_duel: 1 };
  const mine = (all ?? []).filter((p) => p.steamId === steamId);
  const counts = new Map<PraiseType, number>();
  for (const p of mine) counts.set(p.type, (counts.get(p.type) ?? 0) + 1);
  return {
    list: [...mine].sort((a, b) => rank[b.type] - rank[a.type] || a.round - b.round),
    counts: [...counts.entries()].sort((a, b) => b[1] - a[1]),
  };
}

export const SEVERITY_ORDER = { high: 0, medium: 1, low: 2 } as const;

/** One player's mistakes, worst first, with a count per kind. */
export function mistakesOf(all: Mistake[] | undefined, steamId: string) {
  const mine = (all ?? []).filter((m) => m.steamId === steamId);
  const counts = new Map<MistakeType, number>();
  for (const m of mine) counts.set(m.type, (counts.get(m.type) ?? 0) + 1);
  return {
    list: [...mine].sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity] || a.round - b.round),
    counts: [...counts.entries()].sort((a, b) => b[1] - a[1]),
  };
}
